/**
 * Terms of Service — use alongside Privacy Policy at registration.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Bot, Scale, AlertTriangle, Shield, RefreshCw, Ban, Mail } from 'lucide-react';
import ShieldIcon from './ShieldIcon';

const TermsOfService: React.FC = () => {
    const { t } = useTranslation();

    const sections: { key: string; icon: typeof FileText }[] = [
        { key: 'theService', icon: FileText },
        { key: 'aiAssistive', icon: Bot },
        { key: 'yourResponsibilities', icon: Scale },
        { key: 'disclaimer', icon: AlertTriangle },
        { key: 'privacy', icon: Shield },
        { key: 'changes', icon: RefreshCw },
        { key: 'limitation', icon: Ban },
        { key: 'contact', icon: Mail },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
                <div className="mb-8">
                    <Link
                        to="/"
                        className="mb-6 inline-flex items-center text-secondary-600 transition-colors hover:text-brand-500"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        {t('common.back')}
                    </Link>

                    <div className="mb-4 flex items-center">
                        <span className="mr-4 grid h-11 w-11 shrink-0 overflow-hidden rounded-full bg-brand-500 shadow-sm ring-2 ring-brand-500/25 sm:h-12 sm:w-12">
                            <ShieldIcon className="size-full" />
                        </span>
                        <div>
                            <h1 className="text-3xl font-bold text-secondary-800">{t('terms.title')}</h1>
                            <p className="mt-1 text-secondary-500">
                                {t('terms.lastUpdated')} January 2026
                            </p>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl bg-white p-8 shadow-xl">
                    <div className="prose prose-secondary max-w-none">
                        <p className="mb-8 text-lg text-secondary-600">{t('terms.intro')}</p>

                        {sections.map((section, index) => {
                            const Icon = section.icon;
                            return (
                                <div key={section.key} className={index !== 0 ? 'mt-8' : ''}>
                                    <div className="mb-4 flex items-center">
                                        <div className="mr-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-500/12">
                                            <Icon className="h-5 w-5 text-brand-500" />
                                        </div>
                                        <h2 className="m-0 text-xl font-semibold text-secondary-800">
                                            {t(`terms.sections.${section.key}.title`)}
                                        </h2>
                                    </div>
                                    <p className="ml-12 text-secondary-600">{t(`terms.sections.${section.key}.content`)}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-8 space-y-2 text-center text-sm text-secondary-500">
                    <p>{t('terms.contactFooter')}</p>
                    <p>
                        <Link to="/privacy" className="font-medium text-brand-500 hover:text-brand-600 hover:underline">
                            {t('terms.seePrivacy')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;
